/**
 * /well-testing/wells/new — add a well (POST /well-testing/wells,
 * fieldiq-api#1241). A full page, not a modal. On save the new well's own page
 * replaces this one, so Back lands on the register.
 *
 * WT_WELL_WRITE only (admin, office_staff, supervisor). Any other role is told
 * so here rather than handed a form the API would refuse. A 409 duplicate_well
 * shows the API's sentence, which names the existing well, and links to it.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWtWell, useWtCustomers, useWtMeta } from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWriteWells, duplicateWell, wtErrorMessage } from '../../lib/well-testing';
import { useBackToList } from '../downhole/recordPage';
import { WellForm } from './WellForm';
import { WT_EYEBROW, WtEmpty, WtLoading, WtPanel } from './WtBits';

export default function WtNewWellScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/well-testing/wells');
  const { user } = useAuth();
  const canAdd = canWriteWells(user?.role);
  const meta = useWtMeta();
  const customers = useWtCustomers(canAdd);
  const create = useCreateWtWell();
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const metaFailure = queryLoadFailure(meta);
  const customersFailure = queryLoadFailure(customers);

  return (
    <div data-testid="wt-new-well-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WT_EYEBROW} title="New well" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WtPanel eyebrow="Well register" title="Well">
            {!canAdd ? (
              <WtEmpty>Only an admin, office staff or a supervisor can add a well.</WtEmpty>
            ) : meta.isLoading || customers.isLoading ? (
              <WtLoading label="Loading form" />
            ) : metaFailure || customersFailure ? (
              <LoadFailureNotice
                error={queryFailureError(metaFailure ? meta : customers)}
                kind={(metaFailure ?? customersFailure) || 'unreachable'}
                subject={metaFailure ? 'the well form' : 'your customers'}
              />
            ) : (customers.data ?? []).length === 0 ? (
              <WtEmpty>A well belongs to a customer, and there are no customers yet. Add one under Clients first.</WtEmpty>
            ) : (
              <WellForm
                well={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                customers={customers.data ?? []}
                pending={create.isPending}
                error={error}
                duplicateOf={duplicateOf}
                submitLabel="Create well"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  setDuplicateOf(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Well created');
                      if (res.data?.id) navigate(`/well-testing/wells/${res.data.id}`, { replace: true });
                      else navigate('/well-testing/wells', { replace: true });
                    },
                    onError: (err) => {
                      const dup = duplicateWell(err);
                      setError(dup ? dup.message : wtErrorMessage(err, 'The well could not be created.'));
                      setDuplicateOf(dup?.existingWellId ?? null);
                    },
                  });
                }}
              />
            )}
          </WtPanel>
        </div>
      </section>
    </div>
  );
}
